<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'
import { ref, computed, watch, onBeforeUnmount } from "vue";
import SecondaryButton from '@/Components/SecondaryButton.vue'
import { Inertia } from '@inertiajs/inertia';
import SearchSelect from '@/Components/SearchSelect.vue'
import TextInput from '@/Components/TextInput.vue'
import { useTenantTextRules } from '@/helpers'

const props = defineProps({
  logs: Object,
  filters: {
    type: Object,
    default: () => ({ styleNumber: '', employeeId: '', description: '' })
  },
  styleNumbers: {
    type: Array,
    default: () => []
  },
  employees: {
    type: Array,
    default: () => []
  }
});
const { resolveTextRule } = useTenantTextRules();

const styleNumberLabel = computed(() =>
  resolveTextRule('style_number.label', 'Style Number', 'inventory.logs.style_number')
);
const logsTitle = computed(() =>
  resolveTextRule('inventory.logs.title', 'Audit Logs', 'inventory.logs.title')
);

const styleNumber = ref(props.filters?.styleNumber ?? '');
const employeeId = ref(props.filters?.employeeId ?? '');
const description = ref(props.filters?.description ?? '');

function employeeOptionId(emp) {
  return String(emp?.employeeID ?? emp?.employeeid ?? '');
}

let t = null;
function scheduleFetch() {
  if (t) clearTimeout(t);
  t = setTimeout(() => {
    Inertia.get(
      window.location.pathname,
      {
        styleNumber: styleNumber.value?.trim() || undefined,
        employeeId: employeeId.value || undefined,
        description: description.value?.trim() || undefined,
      },
      { preserveState: true, replace: true, preserveScroll: true }
    );
  }, 300);
}

watch(() => props.filters, (filters) => {
  styleNumber.value = filters?.styleNumber ?? '';
  employeeId.value = filters?.employeeId ?? '';
  description.value = filters?.description ?? '';
}, { deep: true });

watch([styleNumber, employeeId, description], scheduleFetch);
onBeforeUnmount(() => { if (t) clearTimeout(t); });

const formattedLogs = computed(() =>
  props.logs.data.map(log => ({
    ...log,
    fullName: `${log.firstname ?? ''} ${log.lastname ?? ''}`.trim(),
    timeFormatted: new Date(log.updated_time * 1000).toLocaleString()
  }))
)

function clearFilter() {
  styleNumber.value = '';
  employeeId.value = '';
  description.value = '';
  scheduleFetch();
}

function goBack() {
  window.history.back();
}

const styleOptions = computed(() =>
  (props.styleNumbers || []).map(s => ({ label: String(s), value: String(s) }))
);

const styleNumberModel = computed({
  get: () => (styleNumber.value && styleNumber.value.trim() !== '' ? styleNumber.value : null),
  set: (v) => { styleNumber.value = v ?? ''; },
});

const employeeOptions = computed(() =>
  (props.employees || []).map(emp => ({
    label: `${emp.firstname ?? ''} ${emp.lastname ?? ''}`.trim(),
    value: employeeOptionId(emp),
  })).filter((opt) => opt.value !== '')
);

const employeeModel = computed({
  get: () => (employeeId.value ? String(employeeId.value) : null),
  set: (v) => { employeeId.value = v ?? ''; },
});
</script>

<template>
  <AppLayout :title="logsTitle">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">{{ logsTitle }}</h2>
    </template>

    <div class="max-w-7xl mx-auto px-6 mt-4">
      <SecondaryButton @click="goBack">← Back</SecondaryButton>
    </div>


    <div class="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto my-6">
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label for="styleNumber" class="block text-sm font-medium text-gray-700 mb-1">
              Filter by {{ styleNumberLabel }}
            </label>
            <SearchSelect v-model="styleNumberModel" :options="styleOptions" :placeholder="styleNumberLabel" />
          </div>

          <div>
            <label for="employeeId" class="block text-sm font-medium text-gray-700 mb-1">
              Filter by Employee
            </label>
            <SearchSelect
              v-model="employeeModel"
              :options="employeeOptions"
              placeholder="Employee"
              filterMode="includes"
              :dedupe-by-label="false"
            />
          </div>

          <div>
            <label for="description" class="block text-sm font-medium text-gray-700 mb-1">
              Filter by Description
            </label>
            <TextInput id="description" v-model="description" type="text" class="block w-full" placeholder="Description" />
          </div>

          <div class="flex gap-2">
            <SecondaryButton type="button" class="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-3 rounded-md"
              @click="clearFilter">
              Clear
            </SecondaryButton>
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full w-full text-left text-sm font-light">
          <thead class="border-b font-medium dark:border-neutral-500">
            <tr>
              <th class="px-3 py-2 text-left">Employee</th>
              <th class="px-3 py-2 text-left">Time</th>
              <th class="px-3 py-2 text-left">Log</th>
              <th class="px-3 py-2 text-left">{{ styleNumberLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in formattedLogs" :key="log.id" class="border-b border-gray-200 hover:bg-gray-100">
              <td class="px-3 py-2">{{ log.fullName || 'N/A' }}</td>
              <td class="px-3 py-2">{{ log.timeFormatted }}</td>
              <td class="px-3 py-2">{{ log.log }}</td>
              <td class="px-3 py-2">{{ log.styleNumber || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-6">
        <PaginationLinks :pagination="logs" />
      </div>
    </div>
  </AppLayout>
</template>
